/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PanelLeftOpen, UserRound } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getAdminMobileTitleKey } from "@/lib/navigation/mobile-title";
import { deregisterFcmTokenFromBackend } from "@/lib/firebase/fcm";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useLogoutMutation } from "@/shared/services/useLogoutMutation";
import { setThemePref, useTheme } from "@/shared/theme/useTheme";
import { NotificationPromptBanner } from "@/shared/notifications/components/NotificationPromptBanner";
import { NotificationBell } from "@/shared/notifications/components/NotificationBell";
import { Button, PageHeader, Sidebar, ToastProvider, type SidebarGroup } from "@/shared/rd";
import { PageHeaderSlotTarget } from "./PageHeaderActions";
import { PageHeaderSlotContext, usePageHeaderSlot } from "./page-header-slot";
import { PageTitleContext, type PageTitleOverride } from "./page-title";
import { localizedPath } from "./locale-path";
import { useAdminNav } from "./nav";
import { OfflineState } from "./SystemStates";
import { useOnline } from "./use-online";

type AppShellProps = {
  locale: string;
  isSuperAdmin?: boolean;
  adminId?: string;
  adminName?: string;
  adminRoles?: string[];
  children: React.ReactNode;
};

const loginSuffix = "/admin/login";
const SIDEBAR_COLLAPSED_KEY = "admin_sidebar_collapsed";

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The admin frame, from the admin design system (`shared/rd`): a 240px
 * sidebar beside a column of header + page. On a phone the sidebar floats
 * over a scrim from the header's open button and closes after navigation.
 * Only the page column scrolls, so the header stays put over a long list.
 */
export function AppShell({ locale, isSuperAdmin, adminId, adminName, adminRoles, children }: AppShellProps) {
  const { pathname, search, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const online = useOnline();
  const { resolvedDark } = useTheme();
  const logout = useLogoutMutation();
  const isLoginRoute = pathname?.endsWith(loginSuffix);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  // Any navigation closes the phone sidebar — a link in it, or the back
  // button returning to the page it was opened on. Adjusted during render
  // (not in an effect) so the closed state paints with the new page.
  const [phoneMenu, setPhoneMenu] = useState({ open: false, at: locationKey });
  if (phoneMenu.at !== locationKey) setPhoneMenu({ open: false, at: locationKey });
  const phoneMenuOpen = phoneMenu.open && phoneMenu.at === locationKey;
  const setPhoneMenuOpen = (open: boolean) => setPhoneMenu({ open, at: locationKey });
  const [headerSlot, setHeaderSlot] = usePageHeaderSlot();
  const [titleOverride, setTitleOverride] = useState<PageTitleOverride | null>(null);
  const { groups, activeItem } = useAdminNav({ locale, isSuperAdmin });

  // The shell stays mounted across navigations (only the routed page is keyed
  // on the pathname), so the scroll region would otherwise keep the previous
  // page's offset. A store or restaurant opens in a drawer OVER its list, so
  // `/stores/:id` counts as the list's page: the list keeps its position.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPage = pathname.replace(/\/(stores|restaurants)\/(?!subscriptions\b|new\b)[^/]+$/, "/$1");
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [scrollPage]);

  const fallbackTitle = useMemo(() => t(getAdminMobileTitleKey(pathname)), [pathname, t]);

  const roleLabel = useMemo(() => {
    const roles = adminRoles ?? [];
    if (roles.includes("SUPER_ADMIN")) return t(keys.admin.roleSuperAdmin);
    if (roles.includes("ADMIN")) return t(keys.admin.roleAdmin);
    if (roles.includes("CUSTOMER_SERVICE")) return t(keys.admin.roleCustomerService);
    if (roles.includes("SALES")) return t(keys.admin.roleSales);
    return null;
  }, [adminRoles, t]);

  if (isLoginRoute) {
    return <>{children}</>;
  }

  const s = keys.shell;
  const base = `/${locale}/admin`;
  const profileHref = `${base}/profile`;

  const sidebarGroups: SidebarGroup[] = groups.map((group) => ({
    key: group.key,
    label: group.header,
    badge: group.badge,
    items: group.items.map((item) => ({
      key: item.key,
      label: item.label,
      href: item.href,
      icon: item.icon,
      active: item.isActive,
    })),
  }));

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  const signOut = async () => {
    // Deregister this device's push token first (best-effort).
    await deregisterFcmTokenFromBackend().catch(() => {});
    await logout.mutateAsync();
    navigate(`${base}/login`, { replace: true });
  };

  // A record page names its record; otherwise the active section does.
  const sectionLabel = activeItem?.label ?? fallbackTitle;
  const header = titleOverride
    ? {
        root: activeItem ? sectionLabel : t(s.root),
        page: titleOverride.title,
        description: <RecordSubtitle value={titleOverride} />,
      }
    : {
        root: t(s.root),
        page: sectionLabel,
        description: activeItem?.description,
      };

  const sidebarOpen = isMobile ? phoneMenuOpen : true;
  const sidebar = sidebarOpen ? (
    <Sidebar
      brand={t(s.brand)}
      mark="/trioty-mark-dark.svg"
      markDark="/trioty-mark-light.svg"
      groups={sidebarGroups}
      labels={{
        open: t(s.openSidebar),
        close: t(s.closeSidebar),
        home: t(keys.admin.dashboardTitle),
        search: t(s.searchNav),
        account: t(s.account),
        language: t(keys.common.language),
        theme: t(s.theme),
        light: t(s.themeLight),
        dark: t(s.themeDark),
        signOut: t(keys.common.logout),
        noMatches: t(s.noMatches),
      }}
      accountName={adminName?.trim() || t(keys.common.appName)}
      accountRole={roleLabel}
      language={locale}
      languages={[
        { value: "en", label: "English" },
        { value: "ar", label: "العربية" },
      ]}
      onLanguage={(next) => {
        if (next !== locale) navigate(localizedPath(pathname, search, next), { replace: true });
      }}
      theme={resolvedDark ? "dark" : "light"}
      onTheme={(next) => setThemePref(next)}
      accountItems={[{ key: "profile", label: t(s.profile), href: profileHref, icon: UserRound }]}
      onSignOut={() => void signOut()}
      collapsed={!isMobile && collapsed}
      onToggleCollapsed={toggleCollapsed}
      floating={isMobile}
      onClose={() => setPhoneMenuOpen(false)}
    />
  ) : null;

  return (
    <ToastProvider>
    <div className="rd-root rd-shell admin-rd">
      <a href="#admin-main" className="rd-skip-link">
        {t(keys.common.skipToContent)}
      </a>
      {isMobile && phoneMenuOpen ? <div className="rd-side-scrim" onClick={() => setPhoneMenuOpen(false)} /> : null}
      {sidebar}
      <div className="rd-main">
        <PageHeader
          root={header.root}
          page={header.page}
          description={header.description}
          leading={
            isMobile ? (
              <Button
                variant="ghost"
                iconOnly
                icon={PanelLeftOpen}
                title={t(s.openSidebar)}
                aria-expanded={phoneMenuOpen}
                onClick={() => setPhoneMenuOpen(true)}
                className="rd-flip-rtl"
              />
            ) : undefined
          }
          actions={
            <>
              <PageHeaderSlotTarget onMount={setHeaderSlot} />
              <NotificationBell adminId={adminId} locale={locale} />
            </>
          }
        />
        <div ref={scrollRef} id="admin-main" className="rd-scroll admin-main" data-scroll-container="portal" tabIndex={-1}>
          {online ? null : <OfflineState />}
          {/* Hidden, not unmounted, while offline: a half-filled form survives. */}
          <main className="rd-page" hidden={!online}>
            <NotificationPromptBanner />
            <PageHeaderSlotContext.Provider value={headerSlot}>
              <PageTitleContext.Provider value={setTitleOverride}>{children}</PageTitleContext.Provider>
            </PageHeaderSlotContext.Provider>
          </main>
        </div>
      </div>
    </div>
    </ToastProvider>
  );
}

/**
 * A record's owner, then its LTR token (a phone, a code) as an isolate so a
 * phone's "+" stays in front of its digits on the Arabic page.
 */
function RecordSubtitle({ value }: { value: PageTitleOverride }) {
  if (!value.subtitle && !value.subtitleLtr) return null;
  return (
    <>
      {value.subtitle ? <span dir="auto">{value.subtitle}</span> : null}
      {value.subtitle && value.subtitleLtr ? " · " : null}
      {value.subtitleLtr ? <bdi dir="ltr">{value.subtitleLtr}</bdi> : null}
    </>
  );
}
